/**
 * Luxury Interactions System
 * Scroll reveals · Navbar transparency · Parallax · Image fade
 */
(function LuxuryInteractions() {
  'use strict';

  // ── 1. Navbar transparency ────────────────────────────────────────────────
  function initNavTransparency() {
    const hero = document.getElementById('nh-hero');
    if (!hero) return;

    document.body.classList.add('lux-hero-page');

    function update() {
      const threshold = hero.offsetHeight * 0.55;
      document.body.classList.toggle('lux-scrolled', window.scrollY > threshold);
    }

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  // ── 2. Auto-tag existing section elements for reveal ─────────────────────
  function tagRevealTargets() {
    const selectors = [
      '.catalogue-carousel__header',
      '.catalogue-carousel__track-wrapper',
      '.home-build-cta__content',
      '.home-build-cta__image-col',
      '.custom-why__header',
      '.custom-why__grid',
      '.custom-why__btn',
      '.premium-collage-stats__collage',
      '.premium-collage-stats__content',
      '.premium-split-section__content',
      '.premium-split-section__image-col',
      '.testimonials__header',
    ];

    selectors.forEach(function(sel) {
      document.querySelectorAll(sel).forEach(function(el) {
        if (!el.classList.contains('lux-reveal') && !el.classList.contains('lux-auto-reveal')) {
          el.classList.add('lux-auto-reveal');
        }
      });
    });

    // Testimonial cards with stagger
    var cardGroup = document.querySelector('.testimonials__grid');
    if (cardGroup) {
      cardGroup.classList.add('lux-stagger');
    }
  }

  // ── 3. IntersectionObserver scroll reveals ────────────────────────────────
  function initScrollReveals() {
    var targets = document.querySelectorAll(
      '.lux-reveal, .lux-auto-reveal, .lux-stagger'
    );
    if (!targets.length) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = el.dataset.luxDelay || 0;
        setTimeout(function() {
          el.classList.add('is-visible');
        }, delay * 1000);
        observer.unobserve(el);
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -48px 0px'
    });

    targets.forEach(function(el) {
      observer.observe(el);
    });
  }

  // ── 4. Subtle parallax on hero image ─────────────────────────────────────
  function initParallax() {
    var targets = document.querySelectorAll('[data-parallax]');
    if (!targets.length) return;
    if (window.matchMedia('(max-width: 768px)').matches) return;

    var ticking = false;

    function update() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function() {
        targets.forEach(function(el) {
          var rect = el.getBoundingClientRect();
          if (rect.bottom < -100 || rect.top > window.innerHeight + 100) {
            ticking = false;
            return;
          }
          var progress = rect.top / window.innerHeight;
          var offset = progress * 35;
          el.style.transform = 'translateY(' + offset + 'px)';
        });
        ticking = false;
      });
    }

    window.addEventListener('scroll', update, { passive: true });
  }

  // ── 5. Lazy image fade-in ─────────────────────────────────────────────────
  function initImageFade() {
    document.querySelectorAll('img').forEach(function(img) {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('lux-img-loaded');
      } else {
        img.addEventListener('load', function() {
          img.classList.add('lux-img-loaded');
        }, { once: true });
      }
    });
  }

  // ── 6. Sticky header: hide on scroll down, reveal on scroll up ───────────
  function initStickyHeader() {
    // Prefer the <sticky-header>'s enclosing .shopify-section, fall back to
    // .section-header (the section group wrapper)
    var stickyEl = document.querySelector('sticky-header');
    var section = (stickyEl && stickyEl.closest('.shopify-section')) ||
                  document.querySelector('.section-header') ||
                  document.querySelector('.shopify-section-group-header-group');
    if (!section) return;

    var stickyType = stickyEl && stickyEl.getAttribute('data-sticky-type');
    var alwaysSticky = stickyType === 'always' || stickyType === 'reduce-logo-size';
    var enableHideReveal = !stickyType || stickyType === 'on-scroll-up';

    // Publish header height BEFORE adding the sticky class so the hidden-state
    // offset (top: calc(-1 * var(--header-height))) has a real value to use.
    function updateHeaderHeight() {
      var h = section.offsetHeight;
      if (h > 0) {
        document.documentElement.style.setProperty('--header-height', h + 'px');
      }
    }
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight, { passive: true });
    // Recalculate once more after fonts/images settle
    if (document.readyState !== 'complete') {
      window.addEventListener('load', updateHeaderHeight, { once: true });
    }

    // Baseline: mark as sticky so position:sticky and top:0 (from CSS) apply
    section.classList.add('shopify-section-header-sticky');

    if (!enableHideReveal) return;

    var lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    var ticking = false;
    var DOWN_THRESHOLD = 6;  // pixels — ignore micro deltas downward
    var UP_THRESHOLD = 6;    // pixels — ignore tiny bounce-back upward

    function onScroll() {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(function () {
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        var delta = scrollTop - lastScrollTop;
        var headerHeight = section.offsetHeight || 0;

        if (scrollTop <= headerHeight) {
          // At the top of the page: always show the navbar
          section.classList.remove('shopify-section-header-hidden');
        } else if (delta > DOWN_THRESHOLD && !alwaysSticky) {
          // Scrolling DOWN → hide (slide up off-screen)
          section.classList.add('shopify-section-header-hidden');
        } else if (delta < -UP_THRESHOLD) {
          // Scrolling UP → reveal
          section.classList.remove('shopify-section-header-hidden');
        }

        lastScrollTop = scrollTop;
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ── Init ──────────────────────────────────────────────────────────────────
  function init() {
    initNavTransparency();
    tagRevealTargets();
    initScrollReveals();
    initParallax();
    initImageFade();
    initStickyHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
